import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Sparkles, Mail, Github, Chrome, ChevronDown, ChevronUp } from 'lucide-react';

export default function AuthPage() {
  const navigate = useNavigate();
  const { login, signup, demoLogin, signInWithWorkOS } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showLegacyLogin, setShowLegacyLogin] = useState(false);

  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [signupForm, setSignupForm] = useState({ name: '', email: '', password: '', orgName: '' });

  // WorkOS SSO login
  const handleWorkOSLogin = async () => {
    setLoading(true);
    await signInWithWorkOS();
    // Note: Will redirect to WorkOS, loading state may not clear
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    const success = await login(loginForm.email, loginForm.password);
    setLoading(false);
    if (success) navigate('/feed');
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    const success = await signup(signupForm.name, signupForm.email, signupForm.password, signupForm.orgName);
    setLoading(false);
    if (success) navigate('/onboarding');
  };

  const handleDemo = async () => {
    setLoading(true);
    const success = await demoLogin();
    setLoading(false);
    if (success) navigate('/feed');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted p-4" data-testid="auth-page">
      <div className="w-full max-w-md">
        {/* Logo & Heading */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-500 mb-4">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold mb-2">Focus Pane</h1>
          <p className="text-muted-foreground">Bring your work into one feed</p>
        </div>

        {/* Primary: WorkOS SSO Login */}
        <div className="space-y-4">
          <Button
            className="w-full h-12 text-base"
            onClick={handleWorkOSLogin}
            disabled={loading}
            data-testid="workos-login-btn"
          >
            {loading ? 'Redirecting...' : 'Sign in'}
          </Button>

          {/* Supported providers */}
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <div className="flex-1 h-px bg-border" />
            <span>Sign in with</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <div className="flex justify-center gap-8 text-muted-foreground">
            <div className="flex flex-col items-center gap-1">
              <Chrome className="w-5 h-5" />
              <span className="text-xs">Google</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <Github className="w-5 h-5" />
              <span className="text-xs">GitHub</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <Mail className="w-5 h-5" />
              <span className="text-xs">Magic Link</span>
            </div>
          </div>
        </div>

        {/* Demo Mode */}
        <div className="mt-8">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">Or</span>
            </div>
          </div>
          <Button
            variant="outline"
            className="w-full mt-4"
            onClick={handleDemo}
            disabled={loading}
            data-testid="demo-login-btn"
          >
            <Sparkles className="mr-2 h-4 w-4" />
            Try Demo
          </Button>
        </div>

        {/* Legacy Login Toggle (for migration period) */}
        <div className="mt-6">
          <button
            type="button"
            className="w-full flex items-center justify-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => setShowLegacyLogin(!showLegacyLogin)}
          >
            {showLegacyLogin ? (
              <>
                <ChevronUp className="w-3 h-3" />
                Hide email/password login
              </>
            ) : (
              <>
                <ChevronDown className="w-3 h-3" />
                Use email/password instead
              </>
            )}
          </button>
        </div>

        {/* Legacy Auth Forms (hidden by default) */}
        {showLegacyLogin && (
          <div className="mt-6 pt-6 border-t">
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
                <TabsTrigger value="signup" data-testid="signup-tab">Sign Up</TabsTrigger>
              </TabsList>

              {/* Login Form */}
              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="you@example.com"
                      value={loginForm.email}
                      onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <Input
                      id="login-password"
                      type="password"
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      required
                      data-testid="login-password-input"
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-btn">
                    {loading ? 'Logging in...' : 'Login'}
                  </Button>
                </form>
              </TabsContent>

              {/* Signup Form */}
              <TabsContent value="signup">
                <form onSubmit={handleSignup} className="space-y-4" data-testid="signup-form">
                  <div className="space-y-2">
                    <Label htmlFor="signup-name">Full Name</Label>
                    <Input
                      id="signup-name"
                      placeholder="John Doe"
                      value={signupForm.name}
                      onChange={(e) => setSignupForm({ ...signupForm, name: e.target.value })}
                      required
                      data-testid="signup-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-email">Email</Label>
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder="you@example.com"
                      value={signupForm.email}
                      onChange={(e) => setSignupForm({ ...signupForm, email: e.target.value })}
                      required
                      data-testid="signup-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-password">Password</Label>
                    <Input
                      id="signup-password"
                      type="password"
                      value={signupForm.password}
                      onChange={(e) => setSignupForm({ ...signupForm, password: e.target.value })}
                      required
                      data-testid="signup-password-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-org">Organization Name</Label>
                    <Input
                      id="signup-org"
                      placeholder="Acme Inc."
                      value={signupForm.orgName}
                      onChange={(e) => setSignupForm({ ...signupForm, orgName: e.target.value })}
                      required
                      data-testid="signup-org-input"
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="signup-submit-btn">
                    {loading ? 'Creating account...' : 'Sign Up'}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </div>
    </div>
  );
}